import { useEffect, useState } from 'react';
import { getFirestore, collection, getDoc, doc, addDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileUploader } from 'react-drag-drop-files';
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import DeleteIcon from '@mui/icons-material/Delete';
import firebaseApp from '../utils/firebaseConfig';

export default function NewEventPage() {
  const [title, setTitle] = useState('');
  const [link, setLink] = useState(''); // New state for the link
  const [saving, setSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [eventImages, setEventImages] = useState([]);
  const [eventId, setEventId] = useState('');
  
  // Acepta también los formatos de iPhone (HEIC/HEIF) y demás variantes comunes;
  // todo se convierte a JPEG antes de subir (ver processImageForUpload).
  const fileTypes = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'HEIC', 'HEIF', 'BMP'];

  const navigate = useNavigate();
  const navLocation = useLocation();

  const defaultEventId = new URLSearchParams(navLocation.search).get('id');

  useEffect(() => {
    const fetchEvent = async () => {
      if (!defaultEventId) return;

      try {
        const db = getFirestore(firebaseApp);
        const eventDocRef = doc(db, 'news', defaultEventId);
        const eventDocSnapshot = await getDoc(eventDocRef);

        if (eventDocSnapshot.exists()) {
          const eventData = eventDocSnapshot.data();
          setEventId(defaultEventId);
          setTitle(eventData.title || '');
          setLink(eventData.link || ''); // Set the link state
          // Normalize images to objects of shape { downloadURL: string }
          const normalizedImages = Array.isArray(eventData.image)
            ? eventData.image.map((img) =>
                typeof img === 'string' ? { downloadURL: img } : { downloadURL: img?.downloadURL || '' }
              )
            : [];
          setEventImages(normalizedImages);
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      }
    };

    fetchEvent();
  }, [defaultEventId]);

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
  };

  const handleLinkChange = (event) => {
    setLink(event.target.value); // Handle the link input change
  };

  const handleFileChange = (file, index) => {
    const updatedFiles = [...eventImages];
    const prev = updatedFiles[index] || {};
    updatedFiles[index] = { ...prev, downloadURL: '', file }; // Ensure we clear URL and attach file
    setEventImages(updatedFiles);
  };

  // Convierte cualquier imagen (incl. HEIC de iPhone) a JPEG y la reduce a máx.
  // 1600px, para que las fotos de celular no fallen ni pesen varios MB.
  // Los GIF se suben tal cual para no perder la animación.
  const processImageForUpload = async (file) => {
    const isGif = /\.gif$/i.test(file.name) || file.type === 'image/gif';
    if (isGif) return { blob: file, ext: 'gif' };

    const objectUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = document.createElement('img');
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = objectUrl;
      });
      const maxDim = 1600;
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.8);
      });
      return { blob, ext: 'jpg' };
    } catch (e) {
      // El navegador no pudo decodificar el archivo (p. ej. HEIC en Chrome de
      // escritorio); se sube el original en vez de bloquear el guardado.
      console.warn('Image conversion failed, uploading original file:', e);
      return { blob: file, ext: null };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  const handleFileUpload = async (file, storageRef) => {
    const { blob, ext } = await processImageForUpload(file);
    const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9._-]/g, '_') || 'image';
    const fileName = `news/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${baseName}${ext ? `.${ext}` : ''}`;
    const fileRef = ref(storageRef, fileName);
    await uploadBytes(fileRef, blob, ext === 'jpg' ? { contentType: 'image/jpeg' } : undefined);
    const downloadURL = await getDownloadURL(fileRef);
    return downloadURL;
  };

  const handleSaveEvent = async () => {
    try {
      // The mobile app requires at least one image per news post — a post with an
      // empty image array crashes the News screen on open. Block saving without one.
      const hasImage = (eventImages || []).some(
        (img) => (img && img.file) || (typeof img === 'string' && img) || (img && img.downloadURL)
      );
      if (!title.trim()) {
        alert('Please enter a title before saving the news.');
        return;
      }
      if (!hasImage) {
        alert('Please add at least one image before saving the news.');
        return;
      }

      setSaving(true);

      const storage = getStorage();
      const storageRef = ref(storage);

      const uploadedImages = await Promise.all(
        (eventImages || []).map(async (image) => {
          // If a new file is present, upload and return its URL object
          if (image && image.file) {
            const fileUploadResult = await handleFileUpload(image.file, storageRef);
            return { downloadURL: fileUploadResult };
          }
          // Support legacy string entries
          if (typeof image === 'string') {
            return { downloadURL: image };
          }
          // If already an object with a URL, keep it
          if (image && typeof image.downloadURL === 'string' && image.downloadURL) {
            return { downloadURL: image.downloadURL };
          }
          return null;
        })
      );

      // Remove any nulls and ensure only plain objects with downloadURL are saved
      const sanitizedImages = uploadedImages.filter(Boolean);

      // Safety net: if every upload failed, do not persist an imageless post.
      if (sanitizedImages.length === 0) {
        setSaving(false);
        alert('Image upload failed. Please try again before saving.');
        return;
      }

      const event = {
        title,
        link, // Include the link in the event object
        image: sanitizedImages,
        createdAt: new Date(),
      };

      const db = getFirestore();
      if (eventId) {
        const eventRef = doc(db, 'news', eventId);
        await updateDoc(eventRef, event);
      } else {
        await addDoc(collection(db, 'news'), event);
      }

      setTitle('');
      setLink(''); // Reset the link state
      setEventImages([]);
      setSaving(false);
      setAlertOpen(true);
      navigate('/dashboard/events');

      console.log('Event saved successfully!');
    } catch (error) {
      console.error('Error saving event:', error);
      setSaving(false);
      alert('Error saving the news. Please try again.');
    }
  };

  const deleteEventImage = (index) => {
    const updatedEventImages = [...eventImages];
    updatedEventImages.splice(index, 1);
    setEventImages(updatedEventImages);
  };

  const addNewImageField = () => {
    setEventImages([...eventImages, { downloadURL: '', file: null }]);
  };

  return (
    <>
      <Helmet>
        <title> News | BBTC </title>
      </Helmet>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            News Information
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">News Details</Typography>
                  <TextField fullWidth label="Title" value={title} onChange={handleTitleChange} />
                  <TextField fullWidth label="Link" value={link} onChange={handleLinkChange} /> {/* New input for the link */}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                {eventImages.map((image, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" style={{ marginBottom: 30 }}>
                          {`Image ${index + 1}`} {index === 0 && '(Required)'}
                        </Typography>
                        <FileUploader handleChange={(file) => handleFileChange(file, index)} name={`file${index}`} types={fileTypes} />
                        {image.downloadURL && (
                          <Box display="flex" flexDirection="column" alignItems="left" style={{ marginTop: 30 }}>
                            <div style={{ position: 'relative', width: 100, height: 100 }}>
                              <img src={image.downloadURL} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={''} />
                              <IconButton
                                onClick={() => deleteEventImage(index)}
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  background: 'white',
                                  color: 'red',
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Button variant="outlined" onClick={addNewImageField}>
                        Add New Image
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" color="primary" onClick={handleSaveEvent}>
                Save News
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
      {saving && (
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255, 255, 255, 0.5)"
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      {alertOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(0, 0, 0, 0.5)"
        >
          <Typography variant="h6" component="div" sx={{ color: '#fff' }}>
            News saved successfully!
          </Typography>
        </Box>
      )}
    </>
  );
}
